import { v4 as uuidv4 } from 'uuid';
import CredentialsRetryProvider from '../../../frontend-common-libs/src/iot/credentials/credentials-retry-provider';
import IOTCredentialsProvider from '../../../frontend-common-libs/src/iot/IOTCredentialsProvider';
import InstrumentNamedShadow from '../../../frontend-common-libs/src/iot/InstrumentNamedShadow';
import InstrumentGroupCredentials from '../../../frontend-common-libs/src/iot/instrument-group-credentials';

type DesiredReservationState = {
  state: {
    desired: { reservation: { userID: string; reserved: boolean; job?: { jobId: string } | null } };
  };
  clientToken: string;
};

type DesiredJobState = {
  state: {
    desired: { reservation: { job: { jobId: string } | null } };
  };
  clientToken: string;
};

export default class ReservationShadow {
  private readonly shadowName = 'reservation';

  private readonly credentialsRetryProvider: CredentialsRetryProvider;

  private instrumentNamedShadow: InstrumentNamedShadow | undefined;

  constructor() {
    const iotCredentialsProvider: IOTCredentialsProvider = new IOTCredentialsProvider();
    this.credentialsRetryProvider = new CredentialsRetryProvider(iotCredentialsProvider, 1);
  }

  private async getInstrumentCredentials(instrumentId: string) {
    try {
      const credentials = await this.credentialsRetryProvider.resolve();
      const { instrumentGroupCredentialsCollection } = credentials;
      return instrumentGroupCredentialsCollection.find((groupCredentials: any) => {
        const instrumentGroupCredentials = new InstrumentGroupCredentials(groupCredentials);
        return instrumentGroupCredentials.isInstrumentInGroup(instrumentId);
      });
    } catch (e: unknown) {
      throw new Error('Error: Failed to find IoT Credentials');
    }
  }

  private getInstrumentCredentialsMap(instrumentIds: string[]): Promise<Record<string, unknown>>[] {
    const instrumentCredsPromises: Promise<Record<string, unknown>>[] = [];
    instrumentIds.forEach(instrumentId => {
      const promise: Promise<Record<string, unknown>> = new Promise(resolve => {
        this.getInstrumentCredentials(instrumentId)
          .then(credentials => {
            resolve({ instrumentId, credentials });
          })
          .catch(error => {
            console.error(error);
            resolve({ instrumentId });
          });
      });
      instrumentCredsPromises.push(promise);
    });

    return instrumentCredsPromises;
  }

  public async get(instrumentId: string): Promise<unknown> {
    const instrumentCredentials = await this.getInstrumentCredentials(instrumentId);
    return new Promise((resolve, reject) => {
      const instrumentNamedShadow: InstrumentNamedShadow = new InstrumentNamedShadow(
        instrumentCredentials
      );
      const subscriptionCallback = (topic: string, payload: unknown): void => {
        const acceptedTopic = `${instrumentId}/shadow/name/${this.shadowName}/get/accepted`;
        if (topic.includes(acceptedTopic)) {
          instrumentNamedShadow.unsubscribeNamedShadow(instrumentId, this.shadowName, 'get');
          resolve((payload as Buffer).toString());
          instrumentNamedShadow.closeWebsocketConnection();
        }
        reject(new Error(`Error getting the ${this.shadowName} shadow state`));
      };
      try {
        instrumentNamedShadow.subscribeToNamedShadow(
          instrumentId,
          this.shadowName,
          'get',
          subscriptionCallback
        );
        instrumentNamedShadow.publishToNamedShadow(instrumentId, this.shadowName, 'get');
      } catch (error) {
        reject(
          new Error(`Something went wrong while trying to get the ${this.shadowName} shadow state`)
        );
      }
    });
  }

  public async updateReservation(
    instrumentId: string,
    reserved: boolean,
    userId = '',
    job: { jobId: string } | null = null
  ): Promise<void> {
    const clientToken = ReservationShadow.generateClientToken();
    const desiredShadowState: DesiredReservationState = {
      state: { desired: { reservation: { userID: userId, reserved, job } } },
      clientToken
    };

    await this.updateNamedShadow(instrumentId, desiredShadowState);
  }

  public async updateJob(instrumentId: string, jobId: string | null): Promise<void> {
    const clientToken = ReservationShadow.generateClientToken();
    const jobSection = jobId ? { jobId } : null;
    const desiredShadowState: DesiredJobState = {
      state: { desired: { reservation: { job: jobSection } } },
      clientToken
    };

    await this.updateNamedShadow(instrumentId, desiredShadowState);
  }

  private async updateNamedShadow(
    instrumentId: string,
    desiredShadowState: DesiredReservationState | DesiredJobState
  ): Promise<void> {
    const instrumentCredentials = await this.getCredentialsForInstrument(instrumentId);
    if (instrumentCredentials?.credentials) {
      const instrumentNamedShadow: InstrumentNamedShadow = new InstrumentNamedShadow(
        instrumentCredentials.credentials as InstrumentGroupCredentials
      );
      try {
        instrumentNamedShadow.updateNamedShadow(instrumentId, this.shadowName, desiredShadowState);
      } catch (e) {
        throw new Error(`Failed to update ${this.shadowName} shadow`);
      }
    } else {
      throw new Error('Failed to find instrument credentials in map');
    }
  }

  private static generateClientToken(): string {
    return `brio-${uuidv4()}`;
  }

  private async getCredentialsForInstrument(instrumentId: string) {
    const credentialsMap = await Promise.all(this.getInstrumentCredentialsMap([instrumentId]));
    return credentialsMap.find(credentialItem => credentialItem.instrumentId === instrumentId);
  }

  public async unsubscribeFromUpdateTopic(instrumentId: string) {
    const instrumentCredentials = await this.getCredentialsForInstrument(instrumentId);
    if (instrumentCredentials?.credentials) {
      if (!this.instrumentNamedShadow) {
        this.instrumentNamedShadow = new InstrumentNamedShadow(
          instrumentCredentials.credentials as InstrumentGroupCredentials
        );
      }
      try {
        this.instrumentNamedShadow.unsubscribeNamedShadow(instrumentId, this.shadowName, 'update');
      } catch (e) {
        throw new Error(
          `Failed to unsubscribe to update topic for ${instrumentId} in ${this.shadowName} shadow`
        );
      }
    } else {
      throw new Error('Failed to find instrument credentials in map');
    }
  }

  public async subscribeToUpdateTopic(
    instrumentId: string,
    callback: (topic: string, payload: unknown) => void
  ): Promise<void> {
    const instrumentCredentials = await this.getCredentialsForInstrument(instrumentId);
    if (instrumentCredentials?.credentials) {
      if (!this.instrumentNamedShadow) {
        this.instrumentNamedShadow = new InstrumentNamedShadow(
          instrumentCredentials.credentials as InstrumentGroupCredentials
        );
      }
      try {
        this.instrumentNamedShadow.subscribeToNamedShadow(
          instrumentId,
          this.shadowName,
          'update',
          callback
        );
      } catch (e) {
        throw new Error(
          `Failed to subscribe to update topic for ${instrumentId} in ${this.shadowName} shadow`
        );
      }
    } else {
      throw new Error('Failed to find instrument credentials in map');
    }
  }

  public closeWebsocketConnection() {
    this.instrumentNamedShadow?.closeWebsocketConnection();
  }
}
